import { useCallback, useEffect, useRef, useState } from "react";
import { Image, Loader2, Megaphone, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = { title: "", body: "", tag: "", image: "", link: "" };
const inputCls =
  "w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text placeholder:text-clay-muted/60 transition-colors duration-300";

export default function AdminNews({ token }) {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const fileRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/news");
      setPosts(data);
    } catch {
      toast.error("Failed to load news");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadImage = async (file) => {
    if (!file) return;
    setImgBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/news/upload", fd, { headers });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Photo attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setImgBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and story are required");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/admin/news/${editId}`, form, { headers });
        toast.success("Post updated");
      } else {
        await api.post("/admin/news", form, { headers });
        toast.success("Post published");
      }
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const edit = (p) => {
    setForm({ title: p.title, body: p.body, tag: p.tag || "", image: p.image || "", link: p.link || "" });
    setEditId(p.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/news/${p.id}`, { headers });
      toast.success("Post deleted");
      setPosts((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10 space-y-12">
      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">
          {editId ? "Edit Post" : "Post an Update"}
        </h2>
        <form data-testid="news-form" onSubmit={save} className="border border-clay-border bg-clay-card p-6 sm:p-8 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="news-title-input" placeholder="Headline * — e.g. We're live!" value={form.title} onChange={set("title")} className={inputCls} required />
            <input data-testid="news-tag-input" placeholder="Tag — e.g. Club News, Race Report" value={form.tag} onChange={set("tag")} className={inputCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="news-link-input" type="url" placeholder="Read More link (optional — e.g. a Facebook post URL)" value={form.link} onChange={set("link")} className={inputCls} />
            <div className="flex items-center gap-3">
              <label className={`flex items-center gap-2 border border-dashed border-clay-border hover:border-clay-orange px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] cursor-pointer transition-colors flex-1 justify-center ${imgBusy ? "opacity-50 pointer-events-none" : ""}`}>
                {imgBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                {imgBusy ? "Uploading…" : form.image ? "Change Photo" : "Attach Photo"}
                <input data-testid="news-image-input" ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
              </label>
              {form.image && <img src={form.image} alt="Post" className="h-11 w-16 object-cover border border-clay-border" />}
            </div>
          </div>
          <textarea
            data-testid="news-body-input"
            placeholder="The story * — announcements, race reports, anything for the fans"
            value={form.body}
            onChange={set("body")}
            rows={6}
            className={`${inputCls} resize-y`}
            required
          />
          <div className="flex gap-3">
            {editId && (
              <button
                data-testid="news-cancel-button"
                type="button"
                onClick={() => { setForm(EMPTY); setEditId(null); }}
                className="flex-1 border border-clay-border hover:border-clay-orange font-display font-bold uppercase tracking-wider px-6 py-4 transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <X className="w-5 h-5" /> Cancel
              </button>
            )}
            <button
              data-testid="news-save-button"
              disabled={saving}
              className="flex-[2] bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-6 py-4 transition-colors duration-300 flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Megaphone className="w-5 h-5" />}
              {editId ? "Save Changes" : "Publish Post"}
            </button>
          </div>
        </form>
      </section>

      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">
          Published <span className="text-clay-muted text-xl">({posts.length})</span>
        </h2>
        <div className="border border-clay-border">
          {posts.length === 0 && (
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted py-12 text-center">No posts yet</p>
          )}
          {posts.map((p) => (
            <div
              key={p.id}
              data-testid="news-row"
              className="flex items-start justify-between gap-4 border-b border-clay-border/60 last:border-b-0 px-4 sm:px-6 py-4 hover:bg-clay-card/60 transition-colors"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="font-display font-bold uppercase tracking-tight">{p.title}</p>
                  {p.tag && <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-gold">{p.tag}</span>}
                </div>
                <p className="text-clay-muted text-xs mt-1 line-clamp-2">{p.body}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted mt-1.5">
                  {new Date(p.created_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button data-testid="news-edit-button" onClick={() => edit(p)} className="text-clay-muted hover:text-clay-gold transition-colors p-2" aria-label="Edit post">
                  <Pencil className="w-4 h-4" />
                </button>
                <button data-testid="news-delete-button" onClick={() => remove(p)} className="text-clay-muted hover:text-red-400 transition-colors p-2" aria-label="Delete post">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay-muted mt-4">
          Posts appear on the homepage Latest News section instantly — newest first
        </p>
      </section>
    </div>
  );
}
